/**
 * humanizeError — единая точка превращения технических ошибок (ApiError,
 * сетевые сбои, таймауты агента, сырой stderr) в человекочитаемые сообщения
 * для лент Ассистента и Главного (bd hh-usj).
 *
 * Раньше каждый экран показывал своё `Не удалось сохранить (${status})`, а
 * провалы агента долетали до пользователя как «timeout after 2m0s; stderr:…»
 * или «pollVoiceResult: max attempts (60) reached». Теперь surface-точки
 * (формы, карточки, чат) зовут humanizeError и показывают понятный текст.
 */
import { ApiError } from './client.js';

/** Контекст операции — задаёт запасную формулировку, когда точную не вывести. */
export type ErrorIntent = 'save' | 'load' | 'delete' | 'agent' | 'generic';

const INTENT_FALLBACK: Record<ErrorIntent, string> = {
  save: 'Не получилось сохранить. Попробуй ещё раз.',
  load: 'Не получилось загрузить. Потяни вниз, чтобы обновить.',
  delete: 'Не получилось удалить. Попробуй ещё раз.',
  agent: 'Ассистент не смог обработать команду. Попробуй сформулировать иначе.',
  generic: 'Что-то пошло не так. Попробуй ещё раз.',
};

const MSG_NETWORK = 'Нет связи с сервером. Проверь интернет и попробуй снова.';
const MSG_AGENT_TIMEOUT = 'Ассистент не успел ответить — попробуй повторить команду.';
const MSG_SERVER = 'Сбой на нашей стороне. Мы уже знаем, попробуй позже.';
const MSG_BUSY = 'Сервис сейчас занят. Попробуй через минуту.';
const MSG_FORBIDDEN = 'Нет доступа к этому действию.';

// Технические маркеры — если строка ошибки содержит их, она НЕ показывается
// пользователю как есть; вместо неё берётся человекочитаемый текст.
const NETWORK_PAT = /failed to fetch|networkerror|load failed|err_network|econnrefused/i;
const AGENT_PAT =
  /timeout after|agent did not respond|pollvoiceresult|max attempts|deadline exceeded|exit status|stderr|claude api error/i;
const TECH_PAT = /\{|\}|stderr|exit status|undefined|null\b|http \d|stack trace|[a-z]+error:/i;

/**
 * Берёт человекочитаемое сообщение из тела ApiError, если бэкенд прислал
 * `{ error: "…" }`. Контроллеры отдают по 422/409 кураторский русский текст
 * (напр. «Собственность с именем «X» уже есть») — он лучше любого кода.
 */
function bodyError(body: unknown): string | null {
  if (body !== null && typeof body === 'object' && 'error' in body) {
    const e = (body as { error: unknown }).error;
    if (typeof e === 'string' && e.trim().length > 0 && !TECH_PAT.test(e)) {
      return e.trim();
    }
  }
  return null;
}

function humanizeApiError(err: ApiError, intent: ErrorIntent, fallback: string): string {
  const fromBody = bodyError(err.body);
  const status = err.status;

  if (status === 0) return MSG_NETWORK;
  if (status === 401 || status === 403) return MSG_FORBIDDEN;
  if (status === 404) {
    return intent === 'load' ? 'Не нашли — возможно, объект уже удалён.' : fallback;
  }
  if (status === 408 || status === 429) return MSG_BUSY;
  if (status === 409) {
    return fromBody ?? 'Данные изменились — обнови экран и попробуй снова.';
  }
  if (status === 410) return 'Срок действия истёк — начни заново.';
  if (status === 400 || status === 422) {
    // 422 у форм — это валидация/дубликат; бэкенд формулирует по-русски.
    return fromBody ?? 'Проверь введённые данные и попробуй снова.';
  }
  if (status >= 500) return MSG_SERVER;
  return fromBody ?? fallback;
}

/**
 * Разбирает сырое сообщение не-ApiError ошибки (сетевой сбой, таймаут агента,
 * брошенная строка). Возвращает человекочитаемый текст либо null — тогда
 * вызвавший берёт запасную формулировку по intent.
 */
function humanizeRawMessage(raw: string): string | null {
  const msg = raw.trim();
  if (msg.length === 0) return null;
  if (NETWORK_PAT.test(msg)) return MSG_NETWORK;
  if (AGENT_PAT.test(msg)) return MSG_AGENT_TIMEOUT;
  // Строка с техническими маркерами пользователю не показывается.
  if (TECH_PAT.test(msg)) return null;
  // Уже человеческий короткий русский текст — отдаём как есть.
  if (msg.length <= 160 && /[а-яё]/i.test(msg)) return msg;
  return null;
}

/**
 * Главная функция слоя. Принимает любую ошибку и контекст операции,
 * возвращает строку, которую безопасно показать пользователю.
 */
export function humanizeError(err: unknown, intent: ErrorIntent = 'generic'): string {
  const fallback = INTENT_FALLBACK[intent];
  if (err instanceof ApiError) return humanizeApiError(err, intent, fallback);
  const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return humanizeRawMessage(raw) ?? fallback;
}
